import type { EventHandler, EventName, EventPayload } from "../contracts";

// The central INBOUND dispatcher (architecture.md §4.2). A typed pub/sub: events
// enter at the top (SSE, command side-effects, local UI) and fan out to every
// registered handler. Pure TypeScript — no React, no DOM.
export class EventBus {
    private readonly handlers = new Map<string, Set<EventHandler<unknown>>>();

    on<K extends EventName>(name: K, handler: EventHandler<EventPayload<K>>): () => void {
        let set = this.handlers.get(name);
        if (!set) {
            set = new Set();
            this.handlers.set(name, set);
        }
        set.add(handler as EventHandler<unknown>);
        return () => this.off(name, handler);
    }

    once<K extends EventName>(name: K, handler: EventHandler<EventPayload<K>>): () => void {
        const wrapped: EventHandler<EventPayload<K>> = (payload) => {
            off();
            handler(payload);
        };
        const off = this.on(name, wrapped);
        return off;
    }

    off<K extends EventName>(name: K, handler: EventHandler<EventPayload<K>>): void {
        const set = this.handlers.get(name);
        if (!set) return;
        set.delete(handler as EventHandler<unknown>);
        if (set.size === 0) this.handlers.delete(name);
    }

    emit<K extends EventName>(name: K, payload: EventPayload<K>): void {
        const set = this.handlers.get(name);
        if (!set) return;
        // Snapshot so a handler that (un)subscribes during dispatch can't disturb iteration.
        for (const handler of [...set]) {
            (handler as EventHandler<EventPayload<K>>)(payload);
        }
    }

    clear(name?: EventName): void {
        if (name === undefined) this.handlers.clear();
        else this.handlers.delete(name);
    }

    listenerCount(name: EventName): number {
        return this.handlers.get(name)?.size ?? 0;
    }
}
